import { zodResolver } from '@hookform/resolvers/zod';
import type {
  DesktopPrinter,
  LocalPrinter,
  LocalPrinterList,
  PrinterSetupState,
  PrinterTestOutcome,
} from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconCheck, IconPrinter, IconRefresh } from '@tabler/icons-react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { TILL_UNAVAILABLE } from './till-messages';

/**
 * The till's printer, set on the till — issue #261.
 *
 * The Activity Centre's USB printer printed nothing at handover because the only
 * way to name a printer was `BLOSSOM_PRINTER`, an environment variable an
 * installed till never has. Whoever sets a till up now shares the printer in
 * Windows, types its share name here, and prints a test page before the first
 * visitor. The setting is kept on the device, in the main process, which checks
 * it again — the schema below is a courtesy.
 *
 * On the sign-on screen, in the frame's upper zone beside *Start a shift*: it
 * is about the device rather than about anybody's shift, and nobody has to be
 * signed in to fix a printer.
 */

/** Said on the field when the main process refuses what was typed. */
const NOT_A_PRINTER =
  'A share name is letters, digits, dots, dashes and underscores — or a network printer’s tcp:// address.';

/** The save or the read had no answer, so nothing is known to have changed. */
const PRINTER_UNCHANGED =
  'This till’s software did not respond, and the printer was not changed. Restart the terminal, and report it if it happens again.';

const printerSchema = z.object({
  setting: z
    .string()
    .trim()
    .min(1, 'Enter the printer’s share name.')
    .max(80, 'A share name is at most 80 characters.'),
});

type PrinterValues = z.infer<typeof printerSchema>;

export function PrinterSetup({
  printer,
  listsPrinters = false,
}: {
  printer: DesktopPrinter;
  /** Whether the shell can list this PC's printers (#282). One release behind, it cannot. */
  listsPrinters?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="touch"
        onClick={() => {
          setOpen(true);
        }}
      >
        <IconPrinter />
        Printer
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <FormDialogContent>
          {/* A child, so closing unmounts it: the next time it opens, it reads
              the printer again rather than showing the last test's result. */}
          {open && <PrinterSetupForm printer={printer} listsPrinters={listsPrinters} />}
        </FormDialogContent>
      </Dialog>
    </>
  );
}

function PrinterSetupForm({
  printer,
  listsPrinters,
}: {
  printer: DesktopPrinter;
  listsPrinters: boolean;
}) {
  const form = useForm({
    resolver: zodResolver(printerSchema),
    mode: 'onTouched',
    defaultValues: { setting: '' },
  });

  const [state, setState] = useState<PrinterSetupState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [test, setTest] = useState<PrinterTestOutcome | 'pending' | null>(null);

  const pending = form.formState.isSubmitting;
  const { reset, setValue } = form;
  const setting = useWatch({ control: form.control, name: 'setting' });

  useEffect(() => {
    let current = true;

    printer.setup().then(
      (read) => {
        if (!current) return;
        setState(read);
        reset({ setting: read.setting ?? '' });
      },
      (error: unknown) => {
        if (!current) return;
        console.error('till: the printer setting could not be read', error);
        setFormError(PRINTER_UNCHANGED);
      },
    );

    return () => {
      current = false;
    };
  }, [printer, reset]);

  const handleSubmit = async ({ setting }: PrinterValues) => {
    setFormError(null);
    setTest(null);

    try {
      const outcome = await printer.save(setting);

      if (!outcome.saved) {
        form.setError('setting', { message: NOT_A_PRINTER });
        return;
      }

      setState(outcome.setup);
      reset({ setting: outcome.setup.setting ?? '' });
    } catch (error) {
      console.error('till: the printer setting could not be saved', error);
      setFormError(PRINTER_UNCHANGED);
    }
  };

  const printTestPage = () => {
    setTest('pending');

    printer.testPage().then(setTest, (error: unknown) => {
      console.error('till: the test page could not be asked for', error);
      setTest({
        printed: false,
        transport: state?.status.transport ?? 'none',
        detail: TILL_UNAVAILABLE,
      });
    });
  };

  return (
    <>
      {/* Where output goes now, in the description rather than in a block of
          its own: measured at 1000x625, a separate block put the footer below
          the fold whenever a test page had failed. */}
      <DialogHeader>
        <DialogTitle>Printer</DialogTitle>
        <DialogDescription>
          {state === null ? (
            'Where this till prints tickets and receipts.'
          ) : state.status.configured ? (
            <>
              Tickets and receipts print to{' '}
              <span className="font-mono text-foreground">{state.status.transport}</span>.
            </>
          ) : (
            'No printer is set on this till, so tickets and receipts will not print.'
          )}
        </DialogDescription>
      </DialogHeader>

      {/* Development only: an installed till never has the variable. Said, so
          a setting that seems to change nothing is not a mystery. */}
      {state?.overridden === true && (
        <Alert>
          <IconAlertTriangle />
          <AlertDescription>
            BLOSSOM_PRINTER is set on this machine and decides the printer instead. What is saved
            here is used once it is removed.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          className="grid gap-6"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          {listsPrinters && (
            <PrintersOnThisPc
              printer={printer}
              picked={setting.trim()}
              onPick={(share) => {
                setValue('setting', share, { shouldDirty: true, shouldValidate: true });
              }}
            />
          )}

          <FormField
            control={form.control}
            name="setting"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Share name
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-touch font-mono text-lg`}
                      required
                      placeholder="POS58"
                      autoComplete="off"
                      spellCheck={false}
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Printer properties → Sharing, in Windows. A network printer takes its address
                  instead, like tcp://192.168.1.50:9100.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          {test !== null && test !== 'pending' && <TestPageResult outcome={test} />}

          <DialogFooter>
            {/* The saved printer, through the transport a ticket goes through —
                the main process builds the page, and nothing here prints. With
                no printer set there is nothing to test. */}
            <Button
              type="button"
              variant="outline"
              size="touch"
              disabled={state?.status.configured !== true || test === 'pending' || pending}
              onClick={printTestPage}
            >
              {test === 'pending' ? 'Printing…' : 'Print a test page'}
            </Button>
            <Button type="submit" size="touch" disabled={pending}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}

/**
 * **"Sent", not "printed".** A shared printer's job is taken by the Windows
 * spooler, which takes it from a printer that is off or out of paper too. The
 * paper is the proof, so the sentence points at it.
 */
function TestPageResult({ outcome }: { outcome: PrinterTestOutcome }) {
  if (outcome.printed) {
    return (
      <Alert>
        <IconCheck />
        <AlertTitle>Sent to {outcome.transport}.</AlertTitle>
        <AlertDescription>
          If a page came out and was cut cleanly, the printer is ready. If not, check the share name
          and that the printer is on.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive">
      <IconAlertTriangle />
      <AlertTitle>The test page did not print.</AlertTitle>
      {/* The printer's own words, for whoever is setting the till up — or
          reading them down the phone to somebody who can fix it. */}
      <AlertDescription className="font-mono">{outcome.detail}</AlertDescription>
    </Alert>
  );
}

const LIST_FAILED: LocalPrinterList = { listed: false, reason: 'LIST_FAILED' };

/**
 * The printers Windows has on this PC, and whether each is shared — issue #282.
 *
 * The first till of the rollout had its printer installed and named `BPOS`, was
 * set to `BPOS`, and printed nothing: the printer had never been shared, and a
 * printer name that looks like a share name cannot be told from one until the
 * test page does not come out. So an unshared printer is listed, marked, and
 * cannot be picked, and the line that shares it is shown in full.
 *
 * **An aid, not a replacement.** A `tcp://` printer and a share on another PC
 * are in no local list, so the typed field below is untouched; with no list —
 * off Windows, or when PowerShell could not answer — this is the field alone,
 * as it was. Picking only fills the field in. Saving is still Save.
 */
function PrintersOnThisPc({
  printer,
  picked,
  onPick,
}: {
  printer: DesktopPrinter;
  picked: string;
  onPick: (share: string) => void;
}) {
  // `null` while the first look is out: PowerShell takes seconds to start.
  const [list, setList] = useState<LocalPrinterList | null>(null);
  const [looking, setLooking] = useState(true);

  const look = useCallback(
    () =>
      printer.printers().catch((error: unknown) => {
        console.error('till: the printers on this PC could not be listed', error);
        return LIST_FAILED;
      }),
    [printer],
  );

  useEffect(() => {
    let current = true;

    void look().then((read) => {
      if (!current) return;
      setList(read);
      setLooking(false);
    });

    return () => {
      current = false;
    };
  }, [look]);

  const refresh = () => {
    setLooking(true);

    void look().then((read) => {
      setList(read);
      setLooking(false);
    });
  };

  // Off Windows there is nothing to list, and nothing is said about it.
  if (list?.listed === false && list.reason === 'NOT_WINDOWS') return null;

  return (
    <section aria-labelledby="printers-on-this-pc" className="grid gap-3">
      <div className="flex items-center justify-between gap-4">
        <h3 id="printers-on-this-pc" className={fieldLabel}>
          Printers on this PC
        </h3>
        <Button type="button" variant="outline" size="touch" disabled={looking} onClick={refresh}>
          <IconRefresh />
          {looking ? 'Looking…' : 'Refresh'}
        </Button>
      </div>

      {list === null ? (
        <p className="text-sm text-muted-foreground">Looking for the printers Windows has…</p>
      ) : !list.listed ? (
        <p className="text-sm text-muted-foreground">
          The printers on this PC could not be listed. Type the share name below.
        </p>
      ) : list.printers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No printers are installed in Windows on this PC. Install the printer in Settings →
          Printers &amp; scanners, then press Refresh.
        </p>
      ) : (
        <>
          {/* Once, above the list rather than in every row: a stock Windows PC
              lists Microsoft's PDF, XPS and OneNote printers too, unshared, and
              a paragraph each buried the one at the counter. And it is the part
              that fails silently — an ordinary PowerShell refuses the command. */}
          {list.printers.some((local) => local.share === null) && (
            <p className="text-sm text-muted-foreground">
              A printer that is not shared cannot be printed to. Run its line in PowerShell opened
              as Administrator, then press Refresh.
            </p>
          )}
          <ul className="grid gap-2">
            {list.printers.map((local) => (
              <li key={local.name}>
                <LocalPrinterRow local={local} picked={picked} onPick={onPick} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function LocalPrinterRow({
  local,
  picked,
  onPick,
}: {
  local: LocalPrinter;
  picked: string;
  onPick: (share: string) => void;
}) {
  if (local.share !== null) {
    const { share } = local;
    const chosen = picked === share;

    return (
      <Button
        type="button"
        variant="outline"
        size="touch"
        aria-pressed={chosen}
        className="w-full justify-between"
        onClick={() => {
          onPick(share);
        }}
      >
        <span className="flex items-center gap-2">
          {chosen && <IconCheck />}
          {local.name}
        </span>
        <span className="text-muted-foreground">
          Shared as <span className="font-mono text-foreground">{share}</span>
        </span>
      </Button>
    );
  }

  return (
    <div className="grid gap-2 rounded-2xl border border-border p-3">
      <p className="flex flex-wrap items-baseline justify-between gap-x-4">
        <span className="font-medium">{local.name}</span>
        <span className="text-sm font-medium text-destructive">
          {local.shared ? 'Shared under a name this till cannot use' : 'Not shared'}
        </span>
      </p>
      <ShareCommand command={local.shareCommand} />
    </div>
  );
}

/**
 * The command, whole and copyable. Selected with one tap (`select-all`) as well
 * as copied with the button, because a clipboard can be refused and the text is
 * still there to be read out or typed.
 */
function ShareCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    // Guarded rather than optional-chained: `await undefined` resolves, and the
    // button would then say it had copied something it had not.
    if (typeof navigator.clipboard?.writeText !== 'function') return;

    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex items-start gap-2">
      <code className="min-w-0 flex-1 rounded-xl bg-muted px-3 py-2 font-mono text-sm select-all">
        {/* Each word kept whole: a browser breaks after a hyphen, and
            `-` / `ShareName` on two lines reads as two things to type. */}
        {command.split(' ').map((word, index) => (
          <Fragment key={index}>
            {index > 0 && ' '}
            <span className="whitespace-nowrap">{word}</span>
          </Fragment>
        ))}
      </code>
      <Button type="button" variant="outline" size="touch" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
